import React, { useEffect, useRef } from "react";
import useSWR from "swr";
import { fetcher } from "../../../utilities/_fetcher";
import EditIcon from "../svg/edit.svg";
import UserIcon from "../svg/user.svg";

const getStatusClassName = (status) => {
  switch (status) {
    case "Approved":
      return "approval-status-approved";
    case "Rejected":
      return "approval-status-rejected";
    case "Sent Back":
      return "approval-status-sent-back";
    case "Feedback Given":
      return "approval-status-feedback-given";
    default:
      return "approval-status-default";
  }
};

const ApprovalHistory = ({
  isVisible,
  onClose,
  data = [],
  loading = false,
  title = "Approval History",
  style = {},
  bankInfoId = null,
}) => {
  const containerRef = useRef(null);

  const historyApiUrl = bankInfoId
    ? `${process.env.REACT_APP_API_URL}/api/bank-inclusion/v1/qc-history?bank_info_id=${bankInfoId}`
    : null;

  const { data: historyResponse, error: historyError } = useSWR(
    isVisible && historyApiUrl ? historyApiUrl : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      dedupingInterval: 0,
      onSuccess: (data) => {
        console.log("History API response:", data);
      },
      onError: (error) => {
        console.error("History API error:", error);
      },
    }
  );

  const transformedData = React.useMemo(() => {
    if (!historyResponse || !historyResponse.history) return data;

    return historyResponse.history
      .filter((item) => {
        if (title === "L2 Feedback") {
          return item.is_l2_feedback === true;
        }
        return true;
      })
      .map((item) => ({
        designation: item.role,
        approved_by: item.user_name,
        status: item.status,
        action_time: item.timestamp,
        rejection_reason: item.reasons || null,
        instruction: item.instruction || null,
      }));
  }, [historyResponse, data, title]);

  const isLoading =
    loading || (!!historyApiUrl && !historyResponse && !historyError);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        onClose
      ) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div
      className="approval-history-container"
      ref={containerRef}
      style={style}
    >
      <div className="approval-history-container-inner">
        <div className="approval-history-header">
          <h3 className="approval-history-title">{title}</h3>
        </div>

        {isLoading ? (
          <div className="approval-history-loading">
            {[1, 2, 3].map((item) => (
              <div key={item} className="approval-history-skeleton">
                <div className="skeleton skeleton-icon"></div>
                <div className="skeleton-content">
                  <div className="skeleton skeleton-designation"></div>
                  <div className="skeleton-status-row">
                    <div className="skeleton skeleton-name"></div>
                    <div className="skeleton skeleton-time"></div>
                  </div>
                  {item === 1 && (
                    <>
                      <div className="skeleton skeleton-reason"></div>
                      <div className="skeleton-instruction">
                        <div className="skeleton skeleton-instruction-icon"></div>
                        <div className="skeleton skeleton-instruction-text"></div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : transformedData.length > 0 ? (
          transformedData.map((approval, index) => (
            <ApprovalHistoryItem
              key={index}
              designation={approval.designation}
              name={approval.approved_by}
              status={approval.status}
              rejectionReason={approval.rejection_reason}
              instruction={approval.instruction}
              time={approval.action_time}
            />
          ))
        ) : (
          <div className="no-data-message">
            <p>No {title.toLowerCase()} available</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ApprovalHistoryItem = ({
  designation,
  name,
  status,
  rejectionReason = null,
  instruction = null,
  time,
}) => {
  return (
    <div className="approval-history-item">
      <div className="approval-history-item__icon">
        <img src={UserIcon} alt="" />
      </div>
      <div className="approval-history-item__content">
        <div className="designation">{designation}</div>
        <div className="approval-status-container">
          <div className="approver-name">{name}</div>
          <div className="approval-status-time">
            <span className={getStatusClassName(status)}>{status}</span>
            <span className=""> on {time}</span>
          </div>
        </div>
        {rejectionReason && (
          <>
            <div className="rejection-reason">{rejectionReason}</div>
            {instruction && (
              <div className="instruction-container">
                <img src={EditIcon} alt="Edit" className="instruction-icon" />
                <span className="instruction-text">{instruction}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApprovalHistory;
